/**
 * Terms & Conditions and Privacy Policy text.
 * Facts confirmed by the owner (2026-09-26): claims within 3 days of delivery;
 * no liability figure on the page; destination import duties are included in
 * our price. Everything else is deliberately worded without numbers the owner
 * hasn't confirmed. Have a lawyer review before relying on it.
 */

export type LegalSection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type LegalDoc = {
  slug: 'terms' | 'privacy';
  title: string;
  metaTitle: string;
  metaDescription: string;
  lead: string;
  updated: string;
  sections: LegalSection[];
};

const CONTACT_LINE =
  'Questions: saharicourier@gmail.com or +966 505 216 943, or visit any of our 12 branches.';

export const TERMS: LegalDoc = {
  slug: 'terms',
  title: 'Terms & Conditions',
  metaTitle: 'Terms & Conditions | Sahari Cargo',
  metaDescription:
    'The terms that apply to every Sahari Cargo shipment: quotes, packing, prohibited items, customs, transit times, claims and more.',
  lead:
    'These terms apply to every shipment you book with Sahari Cargo — online, by phone, on WhatsApp or at a branch. Please read them before you send anything.',
  updated: '26 September 2026',
  sections: [
    {
      id: 'about',
      title: 'About these terms',
      paragraphs: [
        'Sahari Cargo ("we", "us") is a cargo and courier company with its head office in Riyadh, Kingdom of Saudi Arabia, and 12 branches across the Kingdom. By booking a shipment with us, you ("the shipper") agree to these terms.',
      ],
    },
    {
      id: 'services',
      title: 'Our services',
      paragraphs: [
        'We offer domestic and international courier, airport to airport air cargo, door-to-door air and sea cargo, and packing. We currently ship to India, Pakistan, the Philippines, Bangladesh, Nepal, Sri Lanka, Malaysia, Singapore, Brunei and the United Kingdom; Malaysia and Brunei by air cargo only.',
      ],
    },
    {
      id: 'quotes',
      title: 'Quotes, weight and price',
      bullets: [
        'A quote is an estimate based on the details you give us and is valid for the period shown on the quote.',
        'The final price is based on the actual weight or the volumetric (size) weight of each box, whichever is higher.',
        'Prices include VAT at the current Saudi rate where it applies.',
      ],
    },
    {
      id: 'responsibilities',
      title: 'Your responsibilities',
      bullets: [
        'Declare the contents and value of every shipment truthfully and completely.',
        'Give correct sender and receiver names, phone numbers and addresses, and the documents customs ask for (for example an ID or passport copy).',
        'If you pack the boxes yourself, pack them well enough for air or sea transport. We may repack a box that is unsafe to ship.',
      ],
    },
    {
      id: 'prohibited',
      title: 'Prohibited and restricted items',
      paragraphs: [
        'We do not carry illegal drugs, explosives, weapons or ammunition, hazardous or flammable chemicals, live animals, cash, counterfeit goods, or any item banned in Saudi Arabia or the destination country. We may refuse, hold or return any shipment we believe breaks these rules, and the shipper is responsible for any fines or costs that result.',
      ],
    },
    {
      id: 'customs',
      title: 'Customs, duties and taxes',
      paragraphs: [
        'We prepare the export documents in Saudi Arabia and handle import clearance at the destination. Import duties and taxes charged by the destination country are included in our price, unless your quote says otherwise. We are not responsible for delays, inspections or decisions made by customs authorities.',
      ],
    },
    {
      id: 'transit',
      title: 'Transit times',
      paragraphs: [
        'Transit times we give (usually 10–15 days) are estimates, not guarantees. Flights, sailings, customs checks, public holidays and weather can cause delays.',
      ],
    },
    {
      id: 'payment',
      title: 'Payment',
      paragraphs: [
        'The amount due and when to pay it are confirmed when you book. We may hold a shipment until it is paid in full.',
      ],
    },
    {
      id: 'inspection',
      title: 'Inspection',
      paragraphs: [
        'For security and customs reasons, we, airlines and customs authorities may open and inspect any shipment. We photograph and number every box when it is packed.',
      ],
    },
    {
      id: 'claims',
      title: 'Loss, damage and claims',
      bullets: [
        'Report any damaged or missing box within 3 days of delivery, with photos of the box and its contents.',
        'Unless you buy cargo insurance, our liability for loss or damage is limited as set out in your booking terms.',
        'We are not liable for loss caused by poor packing by the shipper, undeclared or prohibited items, customs action, or indirect loss (such as lost profit).',
        'Cargo insurance is available — ask for it when you book.',
      ],
    },
    {
      id: 'undeliverable',
      title: 'Undeliverable shipments and storage',
      paragraphs: [
        'If a shipment cannot be delivered (wrong address, receiver unreachable, or refused), we will contact you. Storage charges may apply to shipments that are not collected, and return or re-delivery costs are paid by the shipper.',
      ],
    },
    {
      id: 'cancellations',
      title: 'Cancellations and refunds',
      paragraphs: [
        'You can cancel free of charge before we collect your shipment. After collection, costs already incurred (such as collection and packing) may be deducted from any refund.',
      ],
    },
    {
      id: 'law',
      title: 'Law and disputes',
      paragraphs: [
        'These terms are governed by the laws of the Kingdom of Saudi Arabia, and any dispute will be handled by the competent courts of the Kingdom.',
      ],
    },
    {
      id: 'contact',
      title: 'Changes and contact',
      paragraphs: [
        'We may update these terms; the version on this page on the date you book applies to your shipment.',
        CONTACT_LINE,
      ],
    },
  ],
};

export const PRIVACY: LegalDoc = {
  slug: 'privacy',
  title: 'Privacy Policy',
  metaTitle: 'Privacy Policy | Sahari Cargo',
  metaDescription:
    'How Sahari Cargo collects, uses, shares and protects your personal information, in line with the Saudi Personal Data Protection Law (PDPL).',
  lead:
    "How Sahari Cargo collects, uses and protects your personal information, in line with Saudi Arabia's Personal Data Protection Law (PDPL).",
  updated: '26 September 2026',
  sections: [
    {
      id: 'who',
      title: 'Who we are',
      paragraphs: [
        'Sahari Cargo, with its head office in Riyadh, Kingdom of Saudi Arabia, is responsible for the personal information described here. Contact: saharicourier@gmail.com.',
      ],
    },
    {
      id: 'collect',
      title: 'What we collect',
      bullets: [
        'Sender and receiver details: names, phone numbers, e-mail, pickup and delivery addresses.',
        'Customs documents: ID or passport copies and other documents customs require.',
        'Shipment details: contents, weight, number of boxes, photos of packed boxes and tracking history.',
        'Payment details: amounts and payment method (we do not store full card numbers).',
        'Messages you send us through our contact and quote forms, e-mail or WhatsApp.',
        'Website data: tracking numbers you look up and basic technical data such as browser type.',
      ],
    },
    {
      id: 'use',
      title: 'Why we use it',
      bullets: [
        'To collect, pack, ship, clear customs for and deliver your shipment.',
        'To show tracking updates and contact you or the receiver about the shipment.',
        'To answer your questions and send the quotes you ask for.',
        'To meet legal duties such as customs, tax and record-keeping rules.',
        'To keep our service safe and improve it.',
      ],
    },
    {
      id: 'share',
      title: 'Who we share it with',
      paragraphs: [
        'Only those who need it to move your shipment, or where the law requires it: airlines and shipping lines, customs authorities in Saudi Arabia and the destination country, our delivery partners in the destination country, payment providers, and the IT services that run our systems. We never sell your personal information.',
      ],
    },
    {
      id: 'abroad',
      title: 'Sending data abroad',
      paragraphs: [
        "Because we ship internationally, the receiver's details and shipment information are sent to the destination country (India, Pakistan, the Philippines, Bangladesh, Nepal, Sri Lanka, Malaysia, Singapore, Brunei or the United Kingdom) so the shipment can be cleared and delivered.",
      ],
    },
    {
      id: 'retention',
      title: 'How long we keep it',
      paragraphs: [
        'We keep shipment and customs records for as long as we need them to provide the service and as long as Saudi law requires, and then delete or anonymise them.',
      ],
    },
    {
      id: 'security',
      title: 'How we protect it',
      paragraphs: [
        'Access to personal information is limited to staff who need it, our systems use secure connections, and customs documents are stored in access-controlled systems.',
      ],
    },
    {
      id: 'rights',
      title: 'Your rights',
      paragraphs: [
        'You can ask to see the personal information we hold about you, correct it, or have it deleted when we no longer need it, and you can withdraw consent where we rely on it. E-mail saharicourier@gmail.com. You can also complain to the Saudi Data & AI Authority (SDAIA).',
      ],
    },
    {
      id: 'cookies',
      title: 'Cookies',
      paragraphs: [
        'Our website only uses the cookies and browser storage it needs to work, such as remembering your last tracking search.',
      ],
    },
    {
      id: 'contact',
      title: 'Changes and contact',
      paragraphs: [
        'We may update this policy; the date at the top of the page shows the latest version.',
        CONTACT_LINE,
      ],
    },
  ],
};
